// Polyfill for Safari — React 19 uses requestIdleCallback which Safari doesn't support
if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).requestIdleCallback = (cb: IdleRequestCallback) =>
    setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 50 } as IdleDeadline), 1);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).cancelIdleCallback = (id: number) => clearTimeout(id);
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { App } from './App';
import { TooltipHost } from '@attain/ui-lib/components/base';
import { ApiRequestError } from './api/client';
import { reportUnhandledError } from './store/errorSink';
import '@attain/ui-lib/styles/globals.css';
import './styles/app.css';
// House-style ink tooltip (`.attn-tooltip` + `data-tooltip`) lives in this
// component stylesheet; import it once here so the tooltip is available app-wide
// (not only on pages that render a document/claim-grid), replacing the native
// grey `title=` tooltips everywhere.
import '@attain/ui-lib/components/documents/redlineMove.css';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // The sink itself lives in `store/errorSink` so its decision — banner,
    // or not — is testable without booting the app. `query`/`mutation` are
    // passed whole; only their `meta` is read.
    onError: (error, query) => reportUnhandledError(error, query),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => reportUnhandledError(error, mutation),
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Per-error retry: short-circuit session loss (no point retrying a dead
      // session), preserve the existing budget for transient errors. Do NOT
      // collapse this back to a flat `retry: 1`.
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.code === 'SESSION_EXPIRED') {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {/* One global tooltip controller — positions every `[data-tooltip]`
          within the viewport (supersedes the CSS .attn-tooltip::after). */}
      <TooltipHost />
    </QueryClientProvider>
  </React.StrictMode>,
);
